import React from 'react';
import { Link } from 'react-router-dom';

type HeroAction = {
  label: string;
  to: string;
};

type PageHeroProps = {
  eyebrow: string;
  title: string;
  description?: string;
  typedTitle?: boolean;
  showGrid?: boolean;
  action?: HeroAction;
  titleClassName?: string;
};

const TYPE_SPEED_MS = 34;

const PageHero: React.FC<PageHeroProps> = ({
  eyebrow,
  title,
  description,
  typedTitle = false,
  showGrid = false,
  action,
  titleClassName,
}) => {
  const [typedText, setTypedText] = React.useState(typedTitle ? '' : title);

  React.useEffect(() => {
    if (!typedTitle) {
      setTypedText(title);
      return;
    }

    let index = 0;
    setTypedText('');
    const intervalId = window.setInterval(() => {
      index += 1;
      setTypedText(title.slice(0, index));
      if (index >= title.length) window.clearInterval(intervalId);
    }, TYPE_SPEED_MS);

    return () => window.clearInterval(intervalId);
  }, [typedTitle, title]);

  return (
    <section className="bg-[#0A1628] py-24 relative overflow-hidden">
      {showGrid && (
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      )}

      <div className="pointer-events-none absolute inset-0 opacity-[0.18]">
        <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-teal-500/30 blur-3xl animate-pulse" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl animate-pulse" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {action && (
          <div className="mb-8 flex justify-start opacity-0 animate-[fadeInUp_.55s_ease-out_forwards]">
            <Link
              to={action.to}
              className="inline-flex items-center rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-sm font-medium text-teal-200 transition hover:border-teal-300/50 hover:bg-teal-400/10 hover:text-teal-100"
            >
              {action.label}
            </Link>
          </div>
        )}

        <div className="text-center">
          <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em] mb-4 opacity-0 animate-[fadeInUp_.6s_ease-out_forwards]">
            {eyebrow}
          </p>

          <h1
            className={`text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight max-w-6xl mx-auto opacity-0 animate-[fadeInUp_.6s_ease-out_.15s_forwards] ${
              titleClassName || ''
            }`}
          >
            {typedText}
          </h1>

          {description && (
            <p className="text-white/40 mt-3 max-w-2xl mx-auto text-lg font-light opacity-0 animate-[fadeInUp_.7s_ease-out_.45s_forwards]">
              {description}
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default PageHero;
